<template>
  <Listbox
    v-slot="{ open }"
    v-model="model"
    as="div"
    by="value"
    class="relative w-full"
    :multiple="multiple"
    :disabled="disabled"
  >
    <!-- HEADER -->
    <ListboxButton
      v-slot="{ value }"
      class="
        flex w-full items-center justify-between gap-5 border-b bg-grey-100 pb-[0.6875rem] pl-4 pr-3 pt-4
        transition-colors
        ui-open:border-b-transparent
      "
    >
      <div class="flex w-full items-center justify-between gap-[0.25em] pb-1 transition-all">
        <!-- Label -->
        <span class="pointer-events-none truncate whitespace-nowrap typo-subtitle">
          {{ displayValue }}
        </span>

        <div class="flex items-center gap-4">
          <!-- Amount of selected items -->
          <Transition name="fade" mode="out-in">
            <div
              v-show="value.length"
              class="flex size-[1.875rem] items-center justify-center rounded-sm bg-primary-900 text-white"
            >
              {{ value.length }}
            </div>
          </Transition>

          <!-- Icon Chevron -->
          <IconChevron class="ml-1 size-7 shrink-0 rotate-0 transition-all md:size-7 ui-open:rotate-180" />
        </div>
      </div>
    </ListboxButton>

    <!-- OPTIONS -->
    <Transition :open="open" name="fade">
      <ListboxOptions
        class="
          absolute top-full z-20 max-h-144 w-full max-w-full overflow-hidden overflow-y-auto bg-white pb-4
          shadow-100
        "
        style="clip-path: inset(0px -15px -15px -15px);"
      >
        <div class="px-6 pb-2 pt-6 font-semibold typo-subtitle">
          {{ label }}
        </div>

        <ListboxOption
          v-for="option in options"
          :key="option.label"
          :value="option"
          class="
            flex max-w-full cursor-pointer items-start gap-3 truncate px-6 py-[0.325rem] transition-colors
            typo-subtitle
            hover:bg-grey-100
          "
        >
          <template v-if="option.value !== '' || (!Array.isArray(model) && typeof model === 'object' && model?.value)">
            <!-- Checkbox -->
            <div
              class="flex size-8 shrink-0 items-center justify-center rounded-sm border-2 border-primary-900"
            >
              <IconCheck class="size-4 text-primary-900 opacity-0 transition-opacity ui-selected:opacity-100" />
            </div>
            <!-- Option Label -->
            <span class="mt-1 whitespace-normal max-xs:hyphens-auto">{{ option.optionsLabel }}</span>
          </template>
        </ListboxOption>
      </ListboxOptions>
    </Transition>
  </Listbox>
</template>

<script setup lang="ts">
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/vue'

type OptionValue = {
  label: string
  value: string
} | string | number
export type Option = {
  label: string
  optionsLabel: string
  value: OptionValue
}

const props = withDefaults(
  defineProps<{
    options: Option[]
    label: string
    multiple?: boolean
    disabled?: boolean
  }>(),
  {
    required: true,
    multiple: true,
    disabled: false,
  },
)

const model = defineModel<OptionValue | OptionValue[]>()

const displayValue = computed(() => {
  if (props.multiple || typeof model.value === 'string' || typeof model.value === 'number') {
    return props.label
  } else if (!Array.isArray(model.value)) {
    return model.value?.label ?? props.label
  }
  return props.label
})
</script>
